import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';

import { MatGridList } from '@angular/material/grid-list';

import { MatTableDataSource } from '@angular/material/table';

import {
  AggregateLeadResponse,
  SynapseService,
} from 'src/app/core/services/synapse';

import { Observable, Subject } from 'rxjs';

import { Store } from '@ngrx/store';

import { DatePipe } from '@angular/common';

import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import * as fromStore from 'src/app/store/';

import { User } from 'src/app/models/user.model';
import { canUseSampleData } from 'src/app/shared/utils/sample-data-toggle.util';
import {
  getComcastChartColorByIndex,
  getComcastChartPalette,
} from 'src/app/shared/constants/chart-colors';

import { QoEMetrics } from '../../components/qoe-data-table/qoe-data-table.component.types';

import {
  AccountDeviceData,
  DeviceLeadSummary,
  LeadDetails,
  LeadSummary,
  MetadataEntry,
  Metrics,
  QOELeadNetworkMetrics,
  QualityOfExperience,
} from './qoe-page.component.types';

@Component({
  selector: 'app-qoe-page',
  templateUrl: './qoe-page.component.html',
  providers: [DatePipe],
  standalone: false,
})
export class QoePageComponent implements OnInit, OnDestroy {
  @ViewChild(MatGridList, { static: true }) matGridList!: MatGridList;

  timeOfDayBreakdownTitleProvider: string = 'QOE Events per Carrier';

  accountName: string = '';

  nitelIQData: QualityOfExperience[] = [];

  qoeRawDeviceEvents$: Observable<AccountDeviceData> = null;

  selectedTime: string = '';

  transformedNitelLeadsData: QoEMetrics[] = [];

  showMockData: boolean = false;

  canShowSampleDataToggle = signal(false);

  isNoContent = false;

  loading: boolean = false;

  nitelLeadsData: QOELeadNetworkMetrics[] = []; // Store leads data

  qoeScoresByRegionData: number[] = [];

  qoeScoresByRegionCategories: string[] = [];

  carrierLabels: string[] = [];

  carrierData: number[] = [];

  carrierColors: string[] = [];

  timeOfDayBreakdownData: number[] = [];

  qoeLeadsTableData = new MatTableDataSource<QOELeadNetworkMetrics>();

  timeOfDayBreakdownLabels: string[] = ['12a - 8a', '8a - 4p', '4p - 12a'];

  timeOfDayBreakdownTitle: string = 'Time of Day Breakdown';

  qoeTableData = new MatTableDataSource<QualityOfExperience>();

  private destroy$ = new Subject<void>();

  constructor(
    private synapseService: SynapseService,

    private cdr: ChangeDetectorRef,

    private store: Store<fromStore.State>,

    private datePipe: DatePipe,
  ) {}

  ngOnInit(): void {
    // This page otherwise only reloads in response to user-driven UI events
    // (time range / mock data toggle), and route guards only re-run on
    // navigation - so switching accounts while already mounted here would
    // otherwise leave the previous account's data on screen. This single,
    // persistent subscription both loads the initial data and reloads
    // whenever the account genuinely changes.
    this.store
      .select(fromStore.getCurrentUser)
      .pipe(
        distinctUntilChanged(
          (prev, curr) =>
            (prev?.Account?.Name ?? null) === (curr?.Account?.Name ?? null),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((data: User) => {
        this.accountName = data?.Account?.Name;
        this.canShowSampleDataToggle.set(canUseSampleData(data));
        this.loadIQData();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();

    this.destroy$.complete();
  }

  onChildSelectionChange(value: string): void {
    this.selectedTime = value;

    this.getUserInfoAndLoadData();
  }

  toggleMockData(event: boolean): void {
    this.showMockData = event;

    this.getUserInfoAndLoadData();
  }

  getUserInfoAndLoadData(): void {
    this.loadIQData(); // Refresh data using the already-known account
  }

  isLoadingData = false;

  loadIQData(): void {
    if (this.isLoadingData) {
      return;
    }

    this.isLoadingData = true;

    this.qoeTableData = new MatTableDataSource<QualityOfExperience>();

    this.loading = true;

    this.isNoContent = false;

    this.synapseService
      .fetchAllLeads(this.selectedTime, this.showMockData, this.accountName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: QualityOfExperience[]) => {
          this.nitelIQData = data;
          if (this.nitelIQData?.length) {
            this.updateMetrics();
            this.isNoContent = false;
          } else {
            this.isNoContent = true;
          }
          this.loading = false;
          this.isLoadingData = false; // Ensure isLoadingData is set to false
          this.cdr.detectChanges();
        },
        error: (err: unknown) => {
          console.error('Error fetching metrics:', err);
          this.isNoContent = true;
          this.loading = false;
          this.isLoadingData = false; // Ensure isLoadingData is set to false
          this.cdr.detectChanges();
        },
      });

    this.qoeRawDeviceEvents$ = this.synapseService.getQOELeadsAggregate(
      this.selectedTime,

      this.showMockData,

      this.accountName,
    );
    this.isLoadingData = false;
  }

  private updateMetrics(): void {
    this.calculateTimeOfDayBreakdown();

    this.populateQOETableData();

    this.calculateQosScore();

    this.getEventsCountPerCarrier();
  }

  private calculateTimeOfDayBreakdown(): void {
    const timeBuckets = [0, 0, 0];

    this.nitelIQData.forEach((item) => {
      const formattedTime = this.datePipe.transform(
        item.time_frame,

        'HH',

        'en-US',
      );

      const hour = parseInt(formattedTime, 10);

      if (hour >= 0 && hour < 8) {
        timeBuckets[0] += 1;
      } else if (hour >= 8 && hour < 16) {
        timeBuckets[1] += 1;
      } else {
        timeBuckets[2] += 1;
      }
    });

    this.timeOfDayBreakdownData = timeBuckets;
  }

  getEventsCountPerCarrier(): void {
    const result: { [carrier: string]: number } = {};

    this.nitelIQData.forEach((event) => {
      const { carrier } = event;

      if (!result[carrier]) {
        result[carrier] = 0;
      }

      result[carrier] += 1;
    });

    this.carrierLabels = Object.keys(result);

    this.carrierData = Object.values(result);

    const palette = getComcastChartPalette();
    this.carrierColors = this.showMockData
      ? [
          getComcastChartColorByIndex(0, palette),
          getComcastChartColorByIndex(1, palette),
          getComcastChartColorByIndex(2, palette),
        ]
      : [];
  }

  calculateQosScore(): void {
    const regionCounts: { [region: string]: number } = {};

    this.nitelIQData.forEach((item) => {
      const { region } = item;

      if (!regionCounts[region]) {
        regionCounts[region] = 0;
      }

      regionCounts[region] += 1;
    });

    this.qoeScoresByRegionCategories = Object.keys(regionCounts);

    this.qoeScoresByRegionData = Object.values(regionCounts);
  }

  populateQOETableData(): void {
    const tableData = this.nitelIQData.map((item) => {
      const metrics = item.metadata.reduce(
        (acc: Metrics, metric: MetadataEntry) => ({
          ...acc,

          [metric.property]: metric.value,
        }),

        {} as Metrics,
      );

      return {
        time_frame: item.time_frame,

        edgeName: item.device,

        source: item.source, // Changed from sourceName to source

        timestamp: this.formatDate(String(item.time_frame)),

        carrier: item.carrier,

        region: item.region,

        score: item.score,

        qos: item.qos,

        value: item.value,

        qos_category: item.qos_category,

        datausage: item.datausage,

        anomalies: item.anomalies,

        metadata: item.metadata,

        device: item.device,

        ...metrics,
      };
    });

    this.qoeTableData.data = tableData as QualityOfExperience[];
  }

  populateQOELeadsTableData(
    data: AggregateLeadResponse[] | [],
  ): QOELeadNetworkMetrics[] {
    const devicesMap: { [key: string]: LeadDetails[] } = {};

    (
      data as { account_lead_summaries?: LeadSummary[] }
    ).account_lead_summaries?.forEach((leadSummary: LeadSummary) => {
      const { date }: { date: string } = leadSummary;

      const timeWindow = `${leadSummary.begin_time} - ${leadSummary.end_time}`;

      leadSummary.device_lead_summaries.forEach(
        (deviceSummary: DeviceLeadSummary) => {
          const deviceName = deviceSummary.device_name;

          if (!devicesMap[deviceName]) {
            devicesMap[deviceName] = [];
          }

          devicesMap[deviceName].push({
            metric_date: date,

            time_window: timeWindow,

            lead_count: deviceSummary.lead_count,

            summary: deviceSummary.summary,

            action_label: deviceSummary.action_label,
          });
        },
      );
    });

    return Object.keys(devicesMap).map((deviceName) => ({
      device_name: deviceName,

      details: [devicesMap[deviceName][0]],

      expanded: false,
    }));
  }

  private formatDate(dateString: string): string {
    return this.datePipe.transform(
      dateString,

      'MM/dd/yyyy hh:mm:ss a',

      'en-US',
    );
  }
}
